import type { ArchiveNode, Article, ContentStore, RuntimeConfig, Series, Summary } from "../content/types";

export function renderLayout(config: RuntimeConfig, body: string, preloadImage = "", pageTitle = config.siteTitle): string {
  document.title = pageTitle;
  syncPreload(preloadImage);
  return `
    <header class="site-header">
      <div class="shell">
        <a class="brand" href="/" aria-label="SpaceTree.blog" data-link>
          <div class="logo">
            <svg viewBox="0 0 1080 1080" width="200" height="200" role="img" aria-hidden="true">
              <g class="logo-mark">
                <path d="m 78,903 v -33 h 33 V 705 h 33 v -33 h 231 v 33 h 132 v -33 h 33 V 540 H 507 V 408 h 33 v -66 h 33 V 210 h 99 v 66 h 33 v 33 h 99 v -33 h 66 v -66 h 99 v 66 h 33 v 99 h -33 v 231 h -33 v 264" />
                <path d="m 837,441 v 66 h 66 v -66 z" />
                <path d="m 639,441 h 66 v 66 h -66 z" />
                <path d="m 639,309 v 33" />
                <path d="m 903,309 v 33" />
              </g>
            </svg>
          </div>
          <span class="wordmark">spacetree.blog</span>
        </a>
        <div class="header-utility">
          <form class="site-search" action="/" method="get" role="search">
            <label class="sr-only" for="site-search-input">Search the archive</label>
            <input id="site-search-input" type="search" name="q" placeholder="Search notes, tags, authors, and topics" autocomplete="off" spellcheck="false">
          </form>
          <nav class="social-links" aria-label="SkyPenguinLabs social links">
            <a class="social-link" href="https://github.com/SkyPenguinLabs" aria-label="SkyPenguinLabs on GitHub">${githubIcon()}</a>
            <a class="social-link" href="https://x.com/SkyPenguinLabs" aria-label="SkyPenguinLabs on X">${xIcon()}</a>
            <a class="social-link" href="https://www.instagram.com/skypenguinlabs/" aria-label="SkyPenguinLabs on Instagram">${instagramIcon()}</a>
          </nav>
        </div>
      </div>
    </header>
    <main class="shell">${body}</main>
    <footer class="site-footer">
      <div class="shell footer-row">
        <p>Simplicity &gt; Complexity</p>
        <p class="footer-credit">
          <span>Powered by</span>
          <a class="footer-credit-link" href="https://skypenguinlabs.us/">
            <img src="/static/assets/brand/engineered-by.png" alt="SkyPenguinLabs SpaceTree tools" loading="lazy" decoding="async" width="200" height="200">
          </a>
        </p>
      </div>
    </footer>
  `;
}

export function renderLoading(config: RuntimeConfig): string {
  return renderLayout(config, `<p class="empty app-state">Loading archive...</p>`);
}

export function renderError(config: RuntimeConfig, error: unknown): string {
  const message = error instanceof Error ? error.message : "Unable to load content.";
  return renderLayout(config, `<p class="empty app-state">Unable to load content: ${escapeHTML(message)}</p>`);
}

export function renderArchive(config: RuntimeConfig, store: ContentStore): string {
  const count = store.summaries.length;
  const body = `
    <section class="archive-controls archive-controls-tree">
      <div class="archive-summary">
        <strong>${count}</strong>
        <span>published notes</span>
      </div>
    </section>
    <section class="archive-tree" data-tree-root>
      <ul class="archive-tree-list archive-tree-list-root">
        ${
          store.archiveTree.length
            ? store.archiveTree.map(renderArchiveNode).join("")
            : `<li><p class="empty">No articles are published yet.</p></li>`
        }
      </ul>
      ${store.archiveTree.length ? `<p class="empty empty-search" data-search-empty hidden>No articles match your search.</p>` : ""}
    </section>
  `;
  return renderLayout(config, body);
}

export function renderArticle(config: RuntimeConfig, article: Article): string {
  const body = `
    <article class="article-page">
      <p><a class="back-link" href="/" data-link>Back to archive</a></p>
      <header class="article-header">
        <p class="eyebrow">${formatDateTime(article.published_at)} / ${escapeHTML(article.author)} / ${article.word_count} words</p>
        ${
          article.series_slug
            ? `<div class="series-context">
                <p class="series-context-label">Series</p>
                <p class="series-context-body">
                  <a href="/series/${article.series_slug}" data-link>${escapeHTML(article.series_title || "")}</a>
                  ${article.chapter_title ? `<span>/ ${escapeHTML(article.chapter_title)}</span>` : ""}
                  ${article.series_position ? `<span>/ entry ${article.series_position}</span>` : ""}
                </p>
              </div>`
            : ""
        }
        <h1>${escapeHTML(article.title)}</h1>
        ${article.subtitle ? `<p class="article-subtitle article-subtitle-large">${escapeHTML(article.subtitle)}</p>` : ""}
        <p class="lede">${escapeHTML(article.excerpt)}</p>
        ${
          article.cover_image
            ? `<div class="article-hero-image">
                <img src="${escapeAttribute(article.cover_image)}" alt="${escapeAttribute(article.title)} cover image" loading="eager" decoding="async" fetchpriority="high" width="1600" height="700">
              </div>`
            : ""
        }
        ${article.tags.length ? `<ul class="tags tags-large">${article.tags.map((tag) => `<li>${escapeHTML(tag)}</li>`).join("")}</ul>` : ""}
      </header>
      <div class="prose">${article.html}</div>
      ${article.series_slug ? renderSeriesNav(article) : ""}
    </article>
  `;
  return renderLayout(config, body, article.cover_image, `${article.title} | ${config.siteTitle}`);
}

export function renderSeries(config: RuntimeConfig, series: Series): string {
  const body = `
    <section class="series-page">
      <p><a class="back-link" href="/" data-link>Back to archive</a></p>
      <header class="series-header">
        <p class="series-kicker">Series</p>
        <h1>${escapeHTML(series.title)}</h1>
        <p class="series-count">${series.article_count} entries</p>
      </header>
      ${
        series.root_entries.length
          ? `<section class="series-section">
              <div class="series-section-heading"><h2>Entries</h2></div>
              <ol class="series-entry-list">${series.root_entries.map(renderSeriesEntry).join("")}</ol>
            </section>`
          : ""
      }
      ${series.chapters
        .map(
          (chapter) => `
            <section class="series-section">
              <div class="series-section-heading">
                <p class="series-chapter-index">Chapter ${chapter.position}</p>
                <h2>${escapeHTML(chapter.title)}</h2>
              </div>
              <ol class="series-entry-list">${chapter.entries.map(renderSeriesEntry).join("")}</ol>
            </section>`
        )
        .join("")}
    </section>
  `;
  return renderLayout(config, body, "", `${series.title} | ${config.siteTitle}`);
}

export function renderNotFound(config: RuntimeConfig): string {
  return renderLayout(config, `<p class="empty app-state">Nothing is published at this route.</p>`, "", `Not Found | ${config.siteTitle}`);
}

export function attachArchiveSearch(): void {
  const searchForm = document.querySelector<HTMLFormElement>(".site-search");
  const searchInput = searchForm?.querySelector<HTMLInputElement>("input[name='q']");
  const searchEmpty = document.querySelector<HTMLElement>("[data-search-empty]");
  const treeRoot = document.querySelector<HTMLElement>("[data-tree-root]");

  if (!searchInput || !treeRoot) {
    return;
  }

  const articleNodes = Array.from(treeRoot.querySelectorAll<HTMLElement>(".archive-tree-node-article"));
  const groupNodes = Array.from(treeRoot.querySelectorAll<HTMLElement>(".archive-tree-node-group"));

  const syncSearch = (query: string) => {
    const normalized = query.trim().toLowerCase();
    let visibleCount = 0;

    articleNodes.forEach((node) => {
      const haystack = (node.dataset.searchText || node.querySelector<HTMLElement>("[data-search-item]")?.dataset.searchText || "").toLowerCase();
      const match = normalized === "" || haystack.includes(normalized);
      node.hidden = !match;
      if (match) {
        visibleCount += 1;
      }
    });

    for (let index = groupNodes.length - 1; index >= 0; index -= 1) {
      const group = groupNodes[index];
      const summary = group.querySelector<HTMLElement>(".archive-branch-summary");
      const details = group.querySelector<HTMLDetailsElement>(".archive-branch");
      const haystack = (summary?.dataset.searchText || "").toLowerCase();
      const childArticles = Array.from(group.querySelectorAll<HTMLElement>(".archive-tree-node-article"));
      const hasVisibleChild = childArticles.some((node) => !node.hidden);
      const selfMatch = normalized !== "" && haystack.includes(normalized);
      group.hidden = !selfMatch && !hasVisibleChild;
      if (details) {
        details.open = normalized === "" ? true : selfMatch || hasVisibleChild;
      }
    }

    if (searchEmpty) {
      searchEmpty.hidden = visibleCount !== 0;
    }
  };

  searchForm?.addEventListener("submit", (event) => event.preventDefault());
  const params = new URLSearchParams(window.location.search);
  const initialQuery = params.get("q") || "";
  if (initialQuery && searchInput.value !== initialQuery) {
    searchInput.value = initialQuery;
  }

  syncSearch(searchInput.value);
  searchInput.addEventListener("input", () => syncSearch(searchInput.value));
}

function renderArchiveNode(node: ArchiveNode): string {
  if (node.kind === "group") {
    return `
      <li class="archive-tree-node archive-tree-node-group" data-tree-group>
        <details class="archive-branch" open>
          <summary class="archive-branch-summary" data-search-item data-search-text="${escapeAttribute(node.title)}">
            <span class="archive-branch-caret" aria-hidden="true"></span>
            <span class="archive-node-icon archive-node-icon-folder" aria-hidden="true"></span>
            <span class="archive-branch-title">${node.link ? `<a href="${node.link}" data-link>${escapeHTML(node.title)}</a>` : escapeHTML(node.title)}</span>
            <span class="archive-branch-count">${node.children?.length || 0}</span>
          </summary>
          <ul class="archive-tree-list">${(node.children || []).map(renderArchiveNode).join("")}</ul>
        </details>
      </li>
    `;
  }
  const summary = node.summary;
  const search = summary ? `${node.title} ${summary.subtitle} ${summary.excerpt} ${summary.author} ${summary.tags.join(" ")}` : node.title;
  return `
    <li class="archive-tree-node archive-tree-node-article" data-search-item data-search-text="${escapeAttribute(search)}">
      <a class="archive-leaf" href="/articles/${node.slug}" data-link>
        <span class="archive-branch-caret archive-branch-caret-spacer" aria-hidden="true"></span>
        <span class="archive-node-icon archive-node-icon-file" aria-hidden="true"></span>
        <span class="archive-leaf-title">${escapeHTML(node.title)}</span>
        ${
          summary
            ? `<span class="archive-leaf-meta">
                <span>${formatShortDate(summary.published_at)}</span>
                <span>${summary.word_count} words</span>
                ${summary.chapter_title ? `<span>${escapeHTML(summary.chapter_title)}</span>` : ""}
              </span>`
            : ""
        }
      </a>
    </li>
  `;
}

function renderSeriesEntry(entry: Summary): string {
  return `
    <li class="series-entry-card">
      <span class="series-entry-order">${entry.series_position || ""}</span>
      <div>
        <h3><a href="/articles/${entry.slug}" data-link>${escapeHTML(entry.title)}</a></h3>
        ${entry.subtitle ? `<p>${escapeHTML(entry.subtitle)}</p>` : ""}
      </div>
    </li>
  `;
}

function renderSeriesNav(article: Article): string {
  return `
    <nav class="series-nav" aria-label="Series navigation">
      ${
        article.prev_in_series
          ? `<a class="series-nav-link" href="/articles/${article.prev_in_series.slug}" data-link>
              <span class="series-nav-label">Previous</span>
              <strong>${escapeHTML(article.prev_in_series.title)}</strong>
            </a>`
          : `<span class="series-nav-link is-empty"></span>`
      }
      ${
        article.next_in_series
          ? `<a class="series-nav-link series-nav-link-next" href="/articles/${article.next_in_series.slug}" data-link>
              <span class="series-nav-label">Next</span>
              <strong>${escapeHTML(article.next_in_series.title)}</strong>
            </a>`
          : `<span class="series-nav-link is-empty"></span>`
      }
    </nav>
  `;
}

function syncPreload(image: string): void {
  document.querySelectorAll("link[data-spacetree-preload]").forEach((node) => node.remove());
  if (!image) {
    return;
  }
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = image;
  link.dataset.spacetreePreload = "true";
  document.head.append(link);
}

function formatShortDate(value: string): string {
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(value));
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())} UTC`;
}

function escapeHTML(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#39;");
}

function escapeAttribute(value: string): string {
  return escapeHTML(value);
}

function githubIcon(): string {
  return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2C6.48 2 2 6.58 2 12.23c0 4.52 2.87 8.35 6.84 9.7.5.1.68-.22.68-.49 0-.24-.01-1.04-.01-1.89-2.78.62-3.37-1.2-3.37-1.2-.46-1.2-1.11-1.52-1.11-1.52-.91-.64.07-.63.07-.63 1 .07 1.54 1.06 1.54 1.06.9 1.58 2.35 1.12 2.92.86.09-.67.35-1.12.64-1.38-2.22-.26-4.56-1.14-4.56-5.09 0-1.13.39-2.05 1.03-2.78-.1-.26-.45-1.31.1-2.72 0 0 .84-.28 2.75 1.06A9.3 9.3 0 0 1 12 6.84c.85 0 1.71.12 2.51.36 1.91-1.34 2.75-1.06 2.75-1.06.55 1.41.2 2.46.1 2.72.64.73 1.03 1.65 1.03 2.78 0 3.96-2.34 4.82-4.57 5.08.36.32.69.95.69 1.92 0 1.39-.01 2.5-.01 2.84 0 .27.18.59.69.49A10.24 10.24 0 0 0 22 12.23C22 6.58 17.52 2 12 2Z" /></svg>`;
}

function xIcon(): string {
  return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M18.9 2H22l-6.77 7.74L23.2 22h-6.26l-4.9-7.43L5.53 22H2.4l7.24-8.27L1.8 2h6.42l4.43 6.78L18.9 2Zm-1.1 18h1.73L7.27 3.9H5.42L17.8 20Z" /></svg>`;
}

function instagramIcon(): string {
  return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.8A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95h-8.5ZM17.7 5.2a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM12 6.6A5.4 5.4 0 1 1 6.6 12 5.4 5.4 0 0 1 12 6.6Zm0 1.8A3.6 3.6 0 1 0 15.6 12 3.6 3.6 0 0 0 12 8.4Z" /></svg>`;
}
